import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import { fetchPaysprintCreditReport } from "../../../../../HttpRequest/afterlogin";
import { setCachedCreditReport, parseCreditReportData } from "./creditReportUtils";

const CreditScoreModal = ({
  show,
  onHide,
  onSuccess,
  initialData = {},
}) => {
  const [formData, setFormData] = useState({
    name: "",
    mobile: "",
    documentId: "",
    dateOfBirth: "",
    address: "",
    pincode: "",
  });

  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  const LOADING_STEPS = [
    { title: "Identity Validation", desc: "Validating PAN format & borrower record..." },
    { title: "Bureau Handshake", desc: "Connecting to PaySprint VerifyA2Z Bureau gateway..." },
    { title: "Parsing Tradelines", desc: "Retrieving 48-month payment history & account details..." },
    { title: "Credit Score Synthesis", desc: "Computing financial health index & eligibility..." },
  ];

  useEffect(() => {
    if (show) {
      setErrorMessage("");
      setActiveStep(0);
      setLoading(false);
      setFormData({
        name: initialData.name || (initialData.firstName ? `${initialData.firstName || ""} ${initialData.lastName || ""}`.trim() : ""),
        mobile: initialData.mobile || initialData.mobileNumber || "",
        documentId: initialData.documentId || initialData.panNumber || "",
        dateOfBirth: initialData.dateOfBirth || initialData.dob || "",
        address: initialData.address || initialData.residenceAddress || "",
        pincode: initialData.pincode || initialData.pinCode || "",
      });
    }
  }, [show, initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "documentId" ? value.toUpperCase() : value,
    }));
  };

  const handleFetchReport = async (e) => {
    e.preventDefault();
    setErrorMessage("");

    // Basic Validation
    if (!formData.name.trim()) {
      setErrorMessage("Please enter borrower's full name as per PAN.");
      return;
    }
    if (!formData.mobile || formData.mobile.replace(/\D/g, "").length < 10) {
      setErrorMessage("Please enter a valid 10-digit mobile number.");
      return;
    }
    if (!formData.documentId || formData.documentId.trim().length !== 10) {
      setErrorMessage("Please enter a valid 10-character PAN / Document ID.");
      return;
    }
    if (!formData.dateOfBirth) {
      setErrorMessage("Please provide date of birth (YYYY-MM-DD).");
      return;
    }
    if (!formData.address.trim()) {
      setErrorMessage("Please enter communication address / locality.");
      return;
    }
    if (!formData.pincode || formData.pincode.trim().length < 6) {
      setErrorMessage("Please enter a valid 6-digit postal pincode.");
      return;
    }

    setLoading(true);
    setActiveStep(1);

    const payload = {
      name: formData.name.trim(),
      mobile: formData.mobile.replace(/\D/g, "").slice(-10),
      documentId: formData.documentId.trim().toUpperCase(),
      dateOfBirth: formData.dateOfBirth.trim(),
      address: formData.address.trim(),
      pincode: formData.pincode.trim(),
      borrowerId: initialData.borrowerId || initialData.id || null,
    };

    // Progression timer for smooth production-grade feedback
    const stepTimer1 = setTimeout(() => setActiveStep(2), 700);
    const stepTimer2 = setTimeout(() => setActiveStep(3), 1400);

    try {
      let result = null;
      try {
        const response = await fetchPaysprintCreditReport(payload);
        if (response && (response.status === 200 || response.data?.success || response.data?.rawResponse)) {
          result = response.data || response;
        }
      } catch (apiErr) {
        console.warn("Live API call encountered error:", apiErr);
      }

      if (!result || (!result.success && !result.rawResponse)) {
        clearTimeout(stepTimer1);
        clearTimeout(stepTimer2);
        setLoading(false);
        setErrorMessage("Unable to fetch credit bureau report at this time. Please verify your details or try again later.");
        return;
      }

      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);
      setActiveStep(4);

      // Persist in local storage
      setCachedCreditReport(result);

      const parsed = parseCreditReportData(result);
      const score = parsed?.score || "Verified";
      const tier = parsed?.scoreTier?.tier || "Prime";

      setTimeout(() => {
        setLoading(false);
        Swal.fire({
          icon: "success",
          title: "Credit Report Verified!",
          html: `<div class="text-center">
            <p class="mb-2">Official Credit Bureau report successfully fetched.</p>
            <div class="badge bg-success px-3 py-2 rounded-pill fs-6 mt-1">Score: ${score} • ${tier} Rating</div>
          </div>`,
          confirmButtonColor: "#0040e0",
          confirmButtonText: "OK",
        });

        if (onSuccess) {
          onSuccess(result);
        }
        onHide();
      }, 500);
    } catch (err) {
      clearTimeout(stepTimer1);
      clearTimeout(stepTimer2);
      setLoading(false);
      setErrorMessage(err?.response?.data?.message || err?.message || "Failed to fetch credit report. Please verify details.");
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered backdrop={loading ? "static" : true} size="lg">
      <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
        {/* Modal Header */}
        <div
          className="modal-header text-white border-0 px-4 py-3"
          style={{ background: "linear-gradient(135deg, #0040e0 0%, #1e40af 100%)" }}
        >
          <div className="d-flex align-items-center gap-3">
            <div
              className="rounded-circle bg-white bg-opacity-20 d-flex align-items-center justify-content-center"
              style={{ width: "42px", height: "42px" }}
            >
              <i className="fa-solid fa-shield-halved fs-5"></i>
            </div>
            <div>
              <h5 className="modal-title fw-bold mb-0 text-white">Fetch Official Bureau Report</h5>
              <small className="text-white text-opacity-75">
                Instant Credit Bureau Pull via PaySprint & CIR VerifyA2Z
              </small>
            </div>
          </div>
          {!loading && (
            <button
              type="button"
              className="btn-close btn-close-white"
              aria-label="Close"
              onClick={onHide}
            />
          )}
        </div>

        {/* Modal Body */}
        <div className="modal-body p-4">
          {loading ? (
            /* Multi-step Loading Animation */
            <div className="py-4 text-center">
              <div
                className="spinner-border text-primary mb-3"
                role="status"
                style={{ width: "3.5rem", height: "3.5rem", borderWidth: "0.25em" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <h5 className="fw-bold text-dark mb-1">
                {LOADING_STEPS[Math.min(activeStep - 1, 3)]?.title || "Processing Bureau Query..."}
              </h5>
              <p className="text-muted small mb-4">
                {LOADING_STEPS[Math.min(activeStep - 1, 3)]?.desc || "Please wait while we establish secure handshake..."}
              </p>

              {/* Progress Steps Timeline */}
              <div className="row g-2 justify-content-center px-4">
                {LOADING_STEPS.map((step, idx) => {
                  const stepNumber = idx + 1;
                  const isDone = activeStep > stepNumber;
                  const isCurrent = activeStep === stepNumber;

                  return (
                    <div className="col-3" key={step.title}>
                      <div
                        className={`p-2 rounded-3 text-center border transition-all ${
                          isDone
                            ? "border-success bg-success bg-opacity-10 text-success"
                            : isCurrent
                            ? "border-primary bg-primary bg-opacity-10 text-primary fw-bold"
                            : "border-light bg-light text-muted opacity-50"
                        }`}
                        style={{ fontSize: "11px" }}
                      >
                        <i
                          className={`fa-solid ${
                            isDone
                              ? "fa-circle-check text-success"
                              : isCurrent
                              ? "fa-spinner fa-spin text-primary"
                              : "fa-circle"
                          } d-block mb-1`}
                        ></i>
                        <span className="d-none d-md-inline">{step.title}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="alert alert-info border-0 rounded-3 mt-4 text-start small d-flex align-items-center gap-2 mb-0">
                <i className="fa-solid fa-lock text-primary fs-5"></i>
                <span>
                  This soft inquiry does not negatively impact your credit bureau score.
                </span>
              </div>
            </div>
          ) : (
            /* Form inputs */
            <form onSubmit={handleFetchReport}>
              {errorMessage && (
                <div className="alert alert-danger border-0 rounded-3 small mb-3 d-flex align-items-center gap-2">
                  <i className="fa-solid fa-triangle-exclamation text-danger"></i>
                  <span>{errorMessage}</span>
                </div>
              )}

              <div className="p-3 bg-light rounded-3 mb-3 border">
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <span className="fw-bold small text-dark">
                    <i className="fa-solid fa-user-check text-primary me-2"></i>
                    Verify Bureau Search Parameters
                  </span>
                  <span className="badge bg-primary bg-opacity-10 text-primary small">
                    Soft Inquiry (No Score Impact)
                  </span>
                </div>
                <p className="text-muted small mb-0" style={{ fontSize: "12px" }}>
                  Please ensure your PAN and contact details match your official KYC identity to retrieve the latest comprehensive CIR credit report.
                </p>
              </div>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-bold text-dark">Full Name (as on PAN)</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fa-solid fa-user text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Full Name as per PAN"
                      required
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-bold text-dark">Mobile Number</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fa-solid fa-phone text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      name="mobile"
                      value={formData.mobile}
                      onChange={handleChange}
                      placeholder="10-digit mobile number"
                      maxLength="10"
                      required
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-bold text-dark">Document ID (PAN Number)</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fa-solid fa-id-card text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 text-uppercase fw-semibold"
                      name="documentId"
                      value={formData.documentId}
                      onChange={handleChange}
                      placeholder="10-character PAN (e.g. ABCDE1234F)"
                      maxLength="10"
                      required
                    />
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-bold text-dark">Date of Birth</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fa-solid fa-calendar text-muted"></i>
                    </span>
                    <input
                      type="date"
                      className="form-control border-start-0"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="col-md-8">
                  <label className="form-label small fw-bold text-dark">Address / Locality</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fa-solid fa-location-dot text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      placeholder="Enter Address / Locality"
                      required
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <label className="form-label small fw-bold text-dark">Pincode</label>
                  <div className="input-group">
                    <span className="input-group-text bg-white border-end-0">
                      <i className="fa-solid fa-map-pin text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleChange}
                      placeholder="6-digit pincode"
                      maxLength="6"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="d-flex justify-content-end gap-2 mt-4 pt-3 border-top">
                <button
                  type="button"
                  className="btn btn-outline-secondary px-4 rounded-3"
                  onClick={onHide}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary px-4 rounded-3 d-inline-flex align-items-center gap-2"
                  style={{ backgroundColor: "#0040e0", borderColor: "#0040e0" }}
                >
                  <i className="fa-solid fa-bolt"></i>
                  Fetch Credit Bureau Report
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default CreditScoreModal;
